import { useLocation } from '@tanstack/react-router'
import { X, ChevronLeft, ChevronRight, ChevronDown, Pin, PinOff } from 'lucide-react'
import { useEffect, useRef, useState, useCallback } from 'react'

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useTabsStore, type Tab } from '@/stores/tabs.store'

export const PATH_TITLES: Record<string, string> = {
  // 仪表盘
  '/dashboard': '仪表盘',

  // 个人中心
  '/pm/my-tasks': '我的任务',
  '/pm/my-stage-tasks': '我的阶段任务',
  '/my/devices': '我的设备',
  '/hr/salaries/my-payslips': '我的工资条',
  '/settings/my-notifications': '通知偏好',
  '/settings/profile': '个人资料',

  // 项目管理
  '/pm/teams': '团队列表',
  '/pm/projects': '项目列表',
  '/pm/projects/new': '新建项目',
  '/pm/issues': '任务列表',
  '/pm/timelogs': '工时记录',
  '/pm/workload-stats': '工作量统计',
  '/pm/workflow-analytics': '工作流分析',
  '/pm/automation-rules': '自动化规则',

  // 知识库
  '/knowledge-base': '知识库',
  '/knowledge-base/search': '知识库搜索',

  // 组织架构
  '/organization': '组织架构图',

  // 人事管理
  '/hr/employees': '员工列表',
  '/hr/departments': '部门管理',
  '/hr/positions': '职位管理',
  '/hr/leaves': '请假管理',
  '/hr/leaves/pending': '待审批请假',
  '/hr/leaves/statistics': '请假统计',
  '/hr/salaries': '薪资管理',
  '/hr/salaries/payslips': '工资条',

  // 财务管理
  '/finance': '财务管理',
  '/finance/accounts': '账户管理',
  '/finance/cash-flows': '现金流',
  '/finance/categories': '分类管理',
  '/finance/reimbursements': '报销管理',
  '/finance/transfers': '转账记录',
  '/finance/currencies': '货币管理',

  // 行政管理
  '/admin/devices': '设备管理',

  // 系统设置
  '/settings': '系统设置',
  '/settings/config': '系统配置',
  '/settings/notifications': '通知设置',
  '/settings/audit-logs': '审计日志',

  // 通知
  '/notifications': '通知中心',
}

export function getPageTitle(path: string): string {
  // 精确匹配
  if (PATH_TITLES[path]) {
    return PATH_TITLES[path]
  }

  // 动态路由匹配 - 项目相关
  if (path.startsWith('/pm/projects/') && path.includes('/gantt')) {
    return '甘特图'
  }
  if (path.startsWith('/pm/projects/') && path.includes('/board')) {
    return '看板'
  }
  if (path.startsWith('/pm/projects/') && path.includes('/members')) {
    return '项目成员'
  }
  if (path.startsWith('/pm/projects/') && path.includes('/milestones')) {
    return '里程碑'
  }
  if (path.startsWith('/pm/projects/')) {
    return '项目详情'
  }

  // 动态路由匹配 - 任务详情
  if (path.startsWith('/pm/issues/')) {
    return '任务详情'
  }

  // 动态路由匹配 - 团队详情
  if (path.startsWith('/pm/teams/')) {
    return '团队详情'
  }

  // 动态路由匹配 - 员工详情
  if (path.startsWith('/hr/employees/')) {
    return '员工详情'
  }

  // 动态路由匹配 - 知识库
  if (path.startsWith('/knowledge-base/documents.')) {
    return '文档详情'
  }
  if (path.startsWith('/knowledge-base/spaces.')) {
    return '知识空间'
  }

  // 默认返回
  return '页面'
}

interface TabItemProps {
  tab: Tab
  isActive: boolean
  onActivate: () => void
  onClose: () => void
  onCloseOthers: () => void
  onCloseLeft: () => void
  onCloseRight: () => void
  onCloseAll: () => void
  onTogglePin: () => void
  canCloseLeft: boolean
  canCloseRight: boolean
  tabCount: number
}

function TabItem({
  tab,
  isActive,
  onActivate,
  onClose,
  onCloseOthers,
  onCloseLeft,
  onCloseRight,
  onCloseAll,
  onTogglePin,
  canCloseLeft,
  canCloseRight,
  tabCount,
}: TabItemProps) {
  const isPinned = !tab.canClose

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div
          onClick={onActivate}
          className={cn(
            'border-border group relative flex h-9 min-w-[120px] max-w-[200px] cursor-pointer items-center gap-2 border-r px-3 text-sm transition-colors',
            isActive
              ? 'bg-background text-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground',
            isPinned && 'min-w-[100px]'
          )}
        >
          {isPinned && <Pin className="h-3 w-3 text-muted-foreground" />}
          <span className="flex-1 truncate">{tab.title}</span>
          {tab.canClose && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              className={cn(
                'flex h-4 w-4 items-center justify-center rounded-sm transition-colors',
                'opacity-0 group-hover:opacity-100',
                isActive && 'opacity-60',
                'hover:bg-destructive/20 hover:text-destructive'
              )}
            >
              <X className="h-3 w-3" />
            </button>
          )}
          {isActive && <div className="bg-primary absolute bottom-0 left-0 right-0 h-0.5" />}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={onTogglePin}>
          {isPinned ? (
            <>
              <PinOff className="mr-2 h-4 w-4" />
              取消固定
            </>
          ) : (
            <>
              <Pin className="mr-2 h-4 w-4" />
              固定标签
            </>
          )}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onClose} disabled={isPinned}>
          关闭
        </ContextMenuItem>
        <ContextMenuItem onClick={onCloseOthers} disabled={tabCount <= 1}>
          关闭其他
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onCloseLeft} disabled={!canCloseLeft}>
          关闭左侧
        </ContextMenuItem>
        <ContextMenuItem onClick={onCloseRight} disabled={!canCloseRight}>
          关闭右侧
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onCloseAll}>关闭全部</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

export function TabBar() {
  const location = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleTabCount, setVisibleTabCount] = useState(10)
  const { tabs, activeTabId, addTab, removeTab, setActiveTab, updateTab, closeOtherTabs, closeAllTabs } =
    useTabsStore()

  // 计算可见标签数量
  const calculateVisibleTabs = useCallback(() => {
    if (!containerRef.current) { return }
    const containerWidth = containerRef.current.offsetWidth - 100 // 减去左右按钮和下拉菜单宽度
    const tabWidth = 160 // 平均标签宽度
    const count = Math.max(3, Math.floor(containerWidth / tabWidth))
    setVisibleTabCount(count)
  }, [])

  useEffect(() => {
    calculateVisibleTabs()
    window.addEventListener('resize', calculateVisibleTabs)
    return () => window.removeEventListener('resize', calculateVisibleTabs)
  }, [calculateVisibleTabs])

  useEffect(() => {
    const path = location.pathname
    if (path === '/login' || path === '/') {
      return
    }
    const title = getPageTitle(path)
    addTab({ path, title })
  }, [location.pathname, addTab])

  const handleActivate = (tab: Tab) => {
    setActiveTab(tab.id)
    window.location.href = tab.path
  }

  const handleClose = (tab: Tab) => {
    if (!tab.canClose) { return }
    const isActive = tab.id === activeTabId
    const tabIndex = tabs.findIndex((t) => t.id === tab.id)
    removeTab(tab.id)
    if (isActive && tabs.length > 1) {
      const newIndex = Math.min(tabIndex, tabs.length - 2)
      const newTab = tabs.filter((t) => t.id !== tab.id)[newIndex]
      if (newTab) {
        window.location.href = newTab.path
      }
    }
  }

  const handleTogglePin = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId)
    if (tab) {
      updateTab(tabId, { canClose: !tab.canClose })
    }
  }

  const handleCloseOthers = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId)
    closeOtherTabs(tabId)
    if (tab) {
      window.location.href = tab.path
    }
  }

  const handleCloseLeft = (tabId: string) => {
    const index = tabs.findIndex((t) => t.id === tabId)
    const tabsToRemove = tabs.slice(0, index).filter((t) => t.canClose)
    tabsToRemove.forEach((t) => removeTab(t.id))
  }

  const handleCloseRight = (tabId: string) => {
    const index = tabs.findIndex((t) => t.id === tabId)
    const tabsToRemove = tabs.slice(index + 1).filter((t) => t.canClose)
    tabsToRemove.forEach((t) => removeTab(t.id))
  }

  const handleCloseAll = () => {
    closeAllTabs()
    window.location.href = '/dashboard'
  }

  const scrollLeft = () => scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' })
  const scrollRight = () => scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' })

  if (tabs.length === 0) {
    return null
  }

  // 分离可见标签和溢出标签
  const visibleTabs = tabs.slice(0, visibleTabCount)
  const overflowTabs = tabs.slice(visibleTabCount)
  const hasOverflow = overflowTabs.length > 0

  return (
    <div ref={containerRef} className="bg-muted/30 flex h-9 items-center border-b">
      <button
        onClick={scrollLeft}
        className="hover:bg-muted flex h-full w-6 items-center justify-center border-r"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div ref={scrollRef} className="scrollbar-none flex flex-1 overflow-x-auto">
        {visibleTabs.map((tab, index) => (
          <TabItem
            key={tab.id}
            tab={tab}
            isActive={tab.id === activeTabId}
            onActivate={() => handleActivate(tab)}
            onClose={() => handleClose(tab)}
            onCloseOthers={() => handleCloseOthers(tab.id)}
            onCloseLeft={() => handleCloseLeft(tab.id)}
            onCloseRight={() => handleCloseRight(tab.id)}
            onCloseAll={handleCloseAll}
            onTogglePin={() => handleTogglePin(tab.id)}
            canCloseLeft={index > 0}
            canCloseRight={index < tabs.length - 1}
            tabCount={tabs.length}
          />
        ))}
      </div>

      {/* 溢出标签下拉菜单 */}
      {hasOverflow && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hover:bg-muted flex h-full items-center gap-1 border-l px-2 text-sm text-muted-foreground">
              <span>+{overflowTabs.length}</span>
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[300px] overflow-y-auto">
            {overflowTabs.map((tab) => (
              <DropdownMenuItem
                key={tab.id}
                onClick={() => handleActivate(tab)}
                className={cn(
                  'flex items-center justify-between gap-2',
                  tab.id === activeTabId && 'bg-accent'
                )}
              >
                <span className="truncate max-w-[200px]">{tab.title}</span>
                {tab.canClose && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClose(tab)
                    }}
                    className="hover:bg-destructive/20 hover:text-destructive rounded p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <button
        onClick={scrollRight}
        className="hover:bg-muted flex h-full w-6 items-center justify-center border-l"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}
